// Intake Types settings — Calyvra Administrators can add types and activate/deactivate them PER coverage year.
// Only types active for the case's coverage year appear when creating a case. Existing cases are unaffected.
import { useState, useEffect, useCallback } from 'react';
import portalApi from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { ListChecks, Plus, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

export function IntakeTypeSettings() {
  const { user } = useAuth();
  const isCalyvraAdmin = (user?.email || '').toLowerCase().endsWith('@calyvrainsurance.com');
  const [types, setTypes] = useState([]);
  const [years, setYears] = useState([]);
  const [yearId, setYearId] = useState('');
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  // Load coverage years once and pick a default (marked default, else first).
  useEffect(() => {
    (async () => {
      try {
        const ys = await portalApi.getCoverageYears();
        setYears(ys || []);
        if (ys && ys.length) {
          const def = ys.find((y) => y.is_default) || ys[0];
          setYearId(def.id);
        }
      } catch (err) { toast.error(err.message); }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!yearId) return;
    setLoading(true);
    try {
      setTypes(await portalApi.getIntakeTypes(yearId));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [yearId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (t) => {
    const next = !t.is_active;
    setTypes((prev) => prev.map((x) => (x.id === t.id ? { ...x, is_active: next } : x)));
    try {
      await portalApi.updateIntakeType(t.id, { is_active: next, coverage_year_id: yearId });
    } catch (err) {
      toast.error(err.message);
      setTypes((prev) => prev.map((x) => (x.id === t.id ? { ...x, is_active: !next } : x)));
    }
  };

  const add = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await portalApi.createIntakeType(newName.trim());
      toast.success('Intake type added');
      setNewName('');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdding(false);
    }
  };

  if (!isCalyvraAdmin) return null;

  const selectedYear = years.find((y) => y.id === yearId);

  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="intake-types-settings">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div className="flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-sky-500" />
          <h2 className="text-lg font-semibold text-slate-900">Settings — Intake Types</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Settings for</span>
          <Select value={yearId} onValueChange={setYearId}>
            <SelectTrigger className="w-[150px]" data-testid="intake-year-select"><SelectValue placeholder="Coverage year" /></SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y.id} value={y.id}>{y.label || y.year}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Toggles apply to <span className="font-medium text-slate-700">{selectedYear ? (selectedYear.label || selectedYear.year) : 'the selected year'}</span> only.
        Only types active for a case's coverage year appear when creating a case. Turning a type off never changes existing cases.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>
      ) : (
        <div className="space-y-2 mb-5">
          {types.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-2 px-3 rounded-lg border border-slate-100 hover:border-slate-200" data-testid={`intake-type-row-${t.name}`}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-800">{t.name}</span>
                {t.is_system && <Badge variant="outline" className="text-slate-400 text-[10px]">system</Badge>}
                {t.is_active
                  ? <Badge className="bg-green-100 text-green-700">Active</Badge>
                  : <Badge className="bg-slate-100 text-slate-500">Inactive</Badge>}
              </div>
              <Switch checked={t.is_active} onCheckedChange={() => toggle(t)} data-testid={`intake-type-toggle-${t.name}`} />
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Add a new intake type" onKeyDown={(e) => e.key === 'Enter' && add()} data-testid="new-intake-type-input" />
        <Button onClick={add} disabled={adding || !newName.trim()} data-testid="add-intake-type-btn">
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" />Add</>}
        </Button>
      </div>
    </div>
  );
}

export default IntakeTypeSettings;
